import { useState, useRef, useEffect } from "react";
import { formatDuration, calculateLoopCount } from "../services/soundEffects";

export default function SoundEffectClipEditor({
  clip,
  onUpdate,
  onDelete,
  onClose,
  disabled = false,
}) {
  const [startTime, setStartTime] = useState(clip?.start_time || 0);
  const [endTime, setEndTime] = useState(clip?.end_time || 0);
  const [volume, setVolume] = useState(clip?.volume ?? 50);
  const [loopEnabled, setLoopEnabled] = useState(clip?.loop_enabled ?? true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  
  const audioRef = useRef(null);

  useEffect(() => {
    if (clip) {
      setStartTime(clip.start_time || 0);
      setEndTime(clip.end_time || 0);
      setVolume(clip.volume ?? 50);
      setLoopEnabled(clip.loop_enabled ?? true);
    }
  }, [clip?.id]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  if (!clip) return null;

  const clipDuration = endTime - startTime;
  const assetDuration = clip.asset?.duration || 0;
  const loopCount = loopEnabled && assetDuration > 0 
    ? calculateLoopCount(assetDuration, clipDuration) 
    : 1;
  const needsLooping = loopEnabled && loopCount > 1;

  const handleStartTimeChange = (value) => {
    const newStart = Math.max(0, Math.min(endTime - 0.1, parseFloat(value) || 0));
    setStartTime(newStart);
  };

  const handleEndTimeChange = (value) => {
    const newEnd = Math.max(startTime + 0.1, parseFloat(value) || 0);
    setEndTime(newEnd);
  };

  const handleVolumeChange = (value) => {
    setVolume(Math.max(0, Math.min(100, parseInt(value) || 0)));
  };

  const handleSave = () => {
    if (onUpdate) {
      onUpdate(clip.id, {
        start_time: startTime,
        end_time: endTime,
        volume,
        loop_enabled: loopEnabled,
      });
    }
  };

  const handleDelete = () => {
    if (window.confirm(`Remove "${clip.asset?.name || "this sound effect"}" from the timeline?`)) {
      if (onDelete) {
        onDelete(clip.id);
      }
    }
  };

  const playPreview = () => {
    const previewUrl = clip.asset?.preview_url;
    if (!previewUrl) return;

    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.onended = () => setIsPlaying(false);
      audioRef.current.onerror = () => {
        setIsPlaying(false);
        setAudioLoading(false);
      };
      audioRef.current.oncanplaythrough = () => setAudioLoading(false);
    }

    if (isPlaying) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    } else {
      setAudioLoading(true);
      audioRef.current.src = previewUrl;
      audioRef.current.volume = volume / 100;
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
          setAudioLoading(false);
        })
        .catch((err) => {
          console.error("Audio playback failed:", err);
          setIsPlaying(false);
          setAudioLoading(false);
        });
    }
  };

  // Check if values have changed
  const hasChanges = 
    startTime !== clip.start_time ||
    endTime !== clip.end_time ||
    volume !== clip.volume ||
    loopEnabled !== clip.loop_enabled;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-lg" aria-hidden="true">🔊</span>
          <h3 className="font-semibold text-gray-900 truncate">
            {clip.asset?.name || "Sound Effect"}
          </h3>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            aria-label="Close editor"
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            ✕
          </button>
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Asset Info */}
        {clip.asset && (
          <div className="text-sm text-gray-500">
            <span>Original duration: {formatDuration(assetDuration)}</span>
            {clip.asset.category && (
              <span className="ml-2">• {clip.asset.category}</span>
            )}
          </div>
        )}

        {/* Time Inputs */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Time
            </label>
            <div className="flex items-center">
              <input
                type="number"
                value={startTime}
                onChange={(e) => handleStartTimeChange(e.target.value)}
                onBlur={handleSave}
                disabled={disabled}
                step="0.1"
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
              />
              <span className="ml-2 text-sm text-gray-500">sec</span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Time
            </label>
            <div className="flex items-center">
              <input
                type="number"
                value={endTime}
                onChange={(e) => handleEndTimeChange(e.target.value)}
                onBlur={handleSave}
                disabled={disabled}
                step="0.1"
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
              />
              <span className="ml-2 text-sm text-gray-500">sec</span>
            </div>
          </div>
        </div>

        {/* Duration Display */}
        <div className="text-sm text-gray-600">
          Clip duration: <span className="font-medium">{formatDuration(clipDuration)}</span>
        </div>

        {/* Volume Slider */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Volume: {volume}%
          </label>
          <input
            type="range"
            value={volume}
            onChange={(e) => handleVolumeChange(e.target.value)}
            onMouseUp={handleSave}
            onTouchEnd={handleSave}
            disabled={disabled}
            min="0"
            max="100"
            className="w-full h-3 sm:h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer disabled:opacity-50"
          />
        </div>

        {/* Loop Toggle */}
        <div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={loopEnabled}
              onChange={(e) => {
                setLoopEnabled(e.target.checked);
                setTimeout(handleSave, 0);
              }}
              disabled={disabled}
              className="w-4 h-4 text-primary-500 rounded focus:ring-primary-500 disabled:opacity-50"
            />
            <span className="text-sm font-medium text-gray-700">Loop audio</span>
          </label>
          {needsLooping && (
            <p className="mt-1 text-xs text-gray-500 ml-7">
              Will loop {loopCount}x to fill duration
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <button
            onClick={playPreview}
            disabled={disabled || !clip.asset?.preview_url}
            className={`flex-1 px-4 py-2 min-h-[44px] rounded-lg font-medium flex items-center justify-center gap-2 transition-colors ${
              isPlaying
                ? "bg-primary-100 text-primary-700"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {audioLoading ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Loading...</span>
              </>
            ) : isPlaying ? (
              <>
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                </svg>
                <span>Pause</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
                <span>Play</span>
              </>
            )}
          </button>

          <button
            onClick={handleDelete}
            disabled={disabled}
            className="px-4 py-2 min-h-[44px] rounded-lg font-medium text-red-600 bg-red-50 hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <span>🗑️</span>
            <span>Remove</span>
          </button>
        </div>

        {/* Unsaved changes indicator */}
        {hasChanges && (
          <p className="text-xs text-amber-600 text-center">
            Changes will be saved automatically
          </p>
        )}
      </div>
    </div>
  );
}
